import { createSelector } from "@reduxjs/toolkit"
import { Address, getAddress, isAddress } from "viem"
import {
  RepayType,
  TransactionItems,
  isBorrowTransactionItem,
  isCreateDebtTransaction,
  isDepositTransactionItem,
  isEVCTransactionItem,
  isLeverageTransaction,
  isRepayTransactionItem,
  isRepayWithSharesTransactionItem,
  isRewardStreamsTransaction,
  isSkimTransactionItem,
  isTransferTransactionItem,
  isVaultSwapDebtTransaction,
  isVaultSwapRepayTransaction,
  isVaultSwapTransaction,
  isWithdrawTransactionItem,
} from "../factories"

export interface AccountVaults {
  account: Address
  vault: Address
}

export const selectTransactionAccountVaults = createSelector(
  [
    (transactions: TransactionItems) => transactions,
    (_, primaryAccount: Address) => primaryAccount,
  ],
  (transactions, primaryAccount) => {
    const accountVaults = new Map<string, AccountVaults>()
    const primaryPrefix = primaryAccount.substring(0, 20).toLowerCase()

    const addVault = (vault: Address, account: Address) => {
      const prefix = account.substring(0, 20).toLowerCase()
      if (primaryPrefix !== prefix) return
      accountVaults.set(`${vault}-${account}`, { account, vault })
    }

    for (const transaction of transactions) {
      try {
        if (!isAddress(transaction.to) || !isAddress(transaction.from)) continue

        const to = getAddress(transaction.to)
        const from = getAddress(transaction.from)
        if (
          isDepositTransactionItem(transaction) ||
          isSkimTransactionItem(transaction) ||
          isBorrowTransactionItem(transaction) ||
          isRepayWithSharesTransactionItem(transaction) ||
          isRepayTransactionItem(transaction) ||
          isWithdrawTransactionItem(transaction) ||
          isEVCTransactionItem(transaction) ||
          isTransferTransactionItem(transaction)
        ) {
          const vault = getAddress(transaction.vault)
          const account = isWithdrawTransactionItem(transaction) ? from : to
          addVault(vault, account)
          if (
            isTransferTransactionItem(transaction) ||
            isBorrowTransactionItem(transaction)
          ) {
            addVault(vault, from)
          }
        }

        if (isRewardStreamsTransaction(transaction)) {
          const vault = getAddress(transaction.rewarded)
          addVault(vault, to)
        }

        if (
          isCreateDebtTransaction(transaction) ||
          isLeverageTransaction(transaction) ||
          "transactions" in transaction
        ) {
          if (transaction.transactions) {
            const accountVaults = selectTransactionAccountVaults(
              transaction.transactions,
              primaryAccount,
            )

            for (const accountVault of accountVaults) {
              addVault(accountVault.vault, accountVault.account)
            }
          }
        }

        if (
          isVaultSwapTransaction(transaction) ||
          isVaultSwapDebtTransaction(transaction) ||
          isVaultSwapRepayTransaction(transaction)
        ) {
          const toVault = getAddress(transaction.toVault)
          const fromVault = getAddress(transaction.fromVault)

          if (
            !isVaultSwapRepayTransaction(transaction) ||
            transaction.type !== RepayType.WALLET
          ) {
            addVault(toVault, from)
            addVault(fromVault, from)
          }
          addVault(toVault, to)
          addVault(fromVault, to)
          addVault(toVault, primaryAccount)
          addVault(fromVault, primaryAccount)
        }
      } catch (error) {
        console.error("Error in selectTransactionAccountVaults:", transaction)
        throw error
      }
    }

    return Array.from(accountVaults.values())
  },
)

export const selectTransactionPositionAccountVaults = createSelector(
  [
    (transactions: TransactionItems) => transactions,
    (_, primaryAccount: Address) => primaryAccount,
  ],
  (transactions, primaryAccount) => {
    return selectTransactionAccountVaults(
      transactions.filter(
        (transaction) =>
          isLeverageTransaction(transaction) ||
          isCreateDebtTransaction(transaction) ||
          isBorrowTransactionItem(transaction),
      ),
      primaryAccount,
    )
  },
)
